import { IconContainerVariant } from '@components/icon-fancy-container/icon-fancy-container';
import { IconAI } from '@components/icons/ai';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { BaseIcon, IconProps } from '@components/icons/base';
import { IconBranch } from '@components/icons/branch';
import { IconCondition } from '@components/icons/condition';
import { IconConvert } from '@components/icons/convert';
import { IconDate } from '@components/icons/date';
import { IconDocumentCreate } from '@components/icons/document-create';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconFormatter } from '@components/icons/formatter';
import { IconHourglass } from '@components/icons/hourglass';
import { IconCode } from '@components/icons/icon-code';
import { IconWaitCondition } from '@components/icons/icon-wait-condition';
import { IconIntegration } from '@components/icons/integration';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconLoopExit } from '@components/icons/loop-exit';
import { IconLoopRestart } from '@components/icons/loop-restart';
import { IconMerge } from '@components/icons/merge';
import { IconSms } from '@components/icons/phone-sms';
import { IconPlaceholder } from '@components/icons/placeholder';
import { IconPlay } from '@components/icons/play';
import { IconTextAnonymize } from '@components/icons/text-anonymize';
import { IconTextExtract } from '@components/icons/text-extract';
import { IconTrigger } from '@components/icons/trigger';
import { IconWebhook } from '@components/icons/webhook';
import { TextProps, Text } from '@components/text';
import { getScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { ACTION_TYPE } from 'api/types';

export const ACTION_TYPES = {
  journey_start: 'journey_start',
  journey_triggers: 'journey_triggers',
  anonymize_text: 'anonymize_text',
  extract_text: 'extract_text',
  send_email: 'send_email',
  send_sms: 'send_sms',
  journey_branch: 'journey_branch',
  branch_base: 'branch_base',
  break_loop: 'break_loop',
  journey_loop: 'journey_loop',
  journey_merge: 'journey_merge',
  wait_for_time: 'wait_for_time',
  wait_until_fixed_date: 'wait_until_fixed_date',
  wait_until_relative_date: 'wait_until_relative_date',
  wait_for_attribute_change: 'wait_for_attribute_change',
  combo_send_form: 'combo_send_form',
  convert_document: 'convert_document',
  create_document: 'create_document',
  calendar_event: 'calendar_event',
  integration: 'integration',
  webhook: 'webhook',
  code: 'code',
  condition: 'condition',
  condition_v2: 'condition_v2',
  placeholder: 'placeholder',
  formatter: 'formatter',
  ask_ai: 'ask_ai',
  ask_ai_agent: 'ask_ai_agent',
  update_fields: 'update_fields',
  add_to_journey: 'add_to_journey',
  update_traveller: 'update_traveller',
  ghost: 'ghost',
  hidden: 'hidden',
  instantiate_form: 'instantiate_form',
} as const;

export type ActionConfig = {
  label: string;
  icon: typeof BaseIcon;
  variant: IconContainerVariant;
};

const { t } = getScopedTranslate({
  en: {
    ask_ai: 'Ask AI',
    ask_ai_agent: 'Ask AI Agent',
    code: 'Code',
    combo_send_form: 'Form',
    condition: 'Condition',
    condition_v2: 'Condition',
    convert_document: 'Convert document to PDF',
    create_document: 'Create document',
    calendar_event: 'Calendar event',
    ghost: 'Ghost',
    branch_base: 'Branch base',
    integration: 'Integration',
    webhook: 'Webhook',
    journey_branch: 'Branch',
    journey_merge: 'Wait for other actions',
    journey_start: 'Start',
    journey_triggers: 'Triggers',
    placeholder: 'Placeholder',
    formatter: 'Formatter',
    send_email: 'Email',
    send_sms: 'SMS',
    journey_loop: 'Loop',
    break_loop: 'Exit Loop',
    wait_for_time: 'Wait for #',
    wait_for_attribute_change: 'Wait for field change',
    wait_until_fixed_date: 'Wait until # date',
    wait_until_relative_date: 'Wait until # date',
    update_fields: 'Journey field/s',
    add_to_journey: 'Add traveller to Journey',
    update_traveller: 'Traveller field/s',
    hidden: 'Hidden',
    instantiate_form: 'Instantiate form',
    anonymize_text: 'Anonymize text',
    extract_text: 'Extract text',
  },
});

export const actionConfig: Record<ACTION_TYPE, ActionConfig> = {
  ask_ai: { label: t('ask_ai'), icon: IconAI, variant: 'indigo' },
  ask_ai_agent: { label: t('ask_ai_agent'), icon: IconAskAI, variant: 'indigo' },
  code: { label: t('code'), icon: IconCode, variant: 'indigo' },
  combo_send_form: { label: t('combo_send_form'), icon: IconForm, variant: 'green' },
  condition: { label: t('condition'), icon: IconCondition, variant: 'teal' },
  condition_v2: { label: t('condition_v2'), icon: IconCondition, variant: 'teal' },
  convert_document: { label: t('convert_document'), icon: IconConvert, variant: 'blue' },
  create_document: { label: t('create_document'), icon: IconDocumentCreate, variant: 'blue' },
  calendar_event: { label: t('calendar_event'), icon: IconDate, variant: 'green' },
  ghost: { label: t('ghost'), icon: IconPlaceholder, variant: 'gray' },
  branch_base: { label: t('branch_base'), icon: IconBranch, variant: 'teal' },
  integration: { label: t('integration'), icon: IconIntegration, variant: 'purple' },
  webhook: { label: t('webhook'), icon: IconWebhook, variant: 'green' },
  journey_branch: { label: t('journey_branch'), icon: IconBranch, variant: 'teal' },
  journey_merge: { label: t('journey_merge'), icon: IconMerge, variant: 'orange' },
  journey_start: { label: t('journey_start'), icon: IconPlay, variant: 'green' },
  journey_triggers: { label: t('journey_triggers'), icon: IconTrigger, variant: 'blue' },
  placeholder: { label: t('placeholder'), icon: IconPlaceholder, variant: 'indigo' },
  formatter: { label: t('formatter'), icon: IconFormatter, variant: 'indigo' },
  send_email: { label: t('send_email'), icon: IconEmail, variant: 'green' },
  send_sms: { label: t('send_sms'), icon: IconSms, variant: 'green' },
  journey_loop: { label: t('journey_loop'), icon: IconLoopRestart, variant: 'teal' },
  break_loop: { label: t('break_loop'), icon: IconLoopExit, variant: 'red' },
  wait_for_time: { label: t('wait_for_time'), icon: IconHourglass, variant: 'orange' },
  wait_for_attribute_change: {
    label: t('wait_for_attribute_change'),
    icon: IconWaitCondition,
    variant: 'orange',
  },
  wait_until_fixed_date: { label: t('wait_until_fixed_date'), icon: IconDate, variant: 'orange' },
  wait_until_relative_date: {
    label: t('wait_until_relative_date'),
    icon: IconDate,
    variant: 'orange',
  },
  update_fields: { label: t('update_fields'), icon: IconAttributeJourney, variant: 'purple' },
  add_to_journey: { label: t('add_to_journey'), icon: IconJourneyItem, variant: 'teal' },
  update_traveller: {
    label: t('update_traveller'),
    icon: IconAttributeTraveller,
    variant: 'purple',
  },
  anonymize_text: { label: t('anonymize_text'), icon: IconTextAnonymize, variant: 'indigo' },
  extract_text: { label: t('extract_text'), icon: IconTextExtract, variant: 'indigo' },
  hidden: { label: t('hidden'), icon: IconPlaceholder, variant: 'gray' },
  instantiate_form: { label: t('instantiate_form'), icon: IconForm, variant: 'green' },
};

const structuralActionTypes = new Set<ACTION_TYPE>([
  'journey_start',
  'journey_branch',
  'branch_base',
  'journey_merge',
]);

export const isStructuralAction = (actionType: ACTION_TYPE): boolean =>
  structuralActionTypes.has(actionType);

export const getActionLabel = (actionType: ACTION_TYPE): string => {
  return actionConfig[actionType]?.label ?? actionType;
};

export const getActionIcon = (actionType: ACTION_TYPE): typeof BaseIcon => {
  return actionConfig[actionType]?.icon ?? IconPlaceholder;
};

export const getActionVariant = (actionType: ACTION_TYPE): IconContainerVariant => {
  return actionConfig[actionType]?.variant ?? 'gray';
};

const variantToIconColor: Record<IconContainerVariant, Color> = {
  gray: 'elementPurple50',
  purple: 'elementPurple80',
  indigo: 'elementIndigo100',
  green: 'elementGreen200',
  blue: 'elementBlue100',
  orange: 'elementOrange100',
  red: 'elementRed100',
  teal: 'elementTeal200',
};

export const getActionIconColor = (actionType: ACTION_TYPE): Color => {
  const variant = getActionVariant(actionType);
  return variantToIconColor[variant];
};

export type ActionIconProps = Pick<IconProps, 'color' | 'size'> & {
  action: ACTION_TYPE;
};

export const ActionIcon = ({ action, color, size }: ActionIconProps) => {
  const config = actionConfig[action];

  if (!config) {
    console.error(`Unable to find icon for action: ${action}`);
    return null;
  }

  const Icon = config.icon;

  return <Icon color={color ?? getActionIconColor(action)} size={size} />;
};

export type ActionLabelProps = Omit<TextProps, 'text' | 'children'> & {
  action: ACTION_TYPE;
};

export const ActionLabel = ({ action, ...rest }: ActionLabelProps) => {
  const label = actionConfig[action]?.label;

  if (!label) {
    console.error(`Unable to find label for action: ${action}`);
    return null;
  }

  return <Text font="regular12" {...rest} text={label} />;
};
